import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Diamond Chat Widget Component
 *
 * An AI-powered chat assistant with context-aware features:
 * - Detects current page and provides relevant quick actions
 * - Shows page-specific suggestions
 * - Passes context to the API for smarter responses
 * - Supports conversation persistence and export
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FaComments, FaTimes, FaPaperPlane, FaRobot, FaUser, FaExpand, FaCompress, FaPlus, FaLightbulb, FaFileExport, FaSearch, FaGem, FaBookmark, FaCheck, FaStickyNote, FaTrash } from 'react-icons/fa';
import { apiClient } from '../utils/apiClient';
import { useChatContext, formatContextForAPI } from '../hooks/useChatContext';
import { useDiamondSearch, parseSearchQuery, formatFiltersDescription } from '../hooks/useDiamondSearch';
import { createChatNote, loadChatNotes, deleteChatNote, exportNoteAsText } from '../hooks/useChatNotes';
// Animations
const fadeIn = keyframes `
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
const slideInFromBottom = keyframes `
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;
const pulse = keyframes `
  0%, 100% {
    box-shadow: 0 8px 24px rgba(147, 51, 234, 0.4);
  }
  50% {
    box-shadow: 0 8px 32px rgba(147, 51, 234, 0.8);
  }
`;
const typingDots = keyframes `
  0%, 60%, 100% {
    opacity: 0.4;
  }
  30% {
    opacity: 1;
  }
`;
const shimmer = keyframes `
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;
// Styled Components
const ChatContainer = styled.div `
  position: fixed;
  bottom: 20px;
  right: 100px;
  z-index: 9998;

  @media (max-width: 768px) {
    display: none;
  }
`;
const MinimizedButton = styled.button `
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #9333ea 0%, #7c3aed 100%);
  border: 2px solid #a855f7;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #ffffff;
  box-shadow: 0 8px 24px rgba(147, 51, 234, 0.4);
  transition: all 0.3s ease;
  animation: ${pulse} 2s infinite;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 12px 32px rgba(147, 51, 234, 0.6);
  }

  &:active {
    transform: scale(0.95);
  }
`;
const ChatWindow = styled.div `
  width: ${props => props.$isExpanded ? '600px' : '400px'};
  height: ${props => props.$isExpanded ? '80vh' : '650px'};
  max-height: ${props => props.$isExpanded ? '800px' : '650px'};
  background: var(--bg-primary);
  border: 2px solid var(--border-color-hover);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.4);
  animation: ${slideInFromBottom} 0.3s ease;
  overflow: hidden;
  transition: width 0.3s ease, height 0.3s ease, max-height 0.3s ease;

  @media (max-width: 768px) {
    width: 100%;
    height: ${props => props.$isExpanded ? '90vh' : '80vh'};
    max-height: ${props => props.$isExpanded ? '90vh' : '80vh'};
  }
`;
const ChatHeader = styled.div `
  background: linear-gradient(135deg, #9333ea 0%, #7c3aed 100%);
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 2px solid #a855f7;
  flex-shrink: 0;
`;
const ChatTitle = styled.div `
  display: flex;
  align-items: center;
  gap: 10px;
  color: #ffffff;
  font-weight: 600;
  font-size: 14px;

  .icon {
    font-size: 18px;
  }

  .title-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .subtitle {
    font-size: 10px;
    opacity: 0.9;
    font-weight: 400;
  }
`;
const HeaderButtons = styled.div `
  display: flex;
  align-items: center;
  gap: 6px;
`;
const HeaderButton = styled.button `
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #ffffff;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  font-size: 12px;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;
const CloseButton = styled(HeaderButton) ``;
const ExpandButton = styled(HeaderButton) ``;
// Context bar showing current page
const ContextBar = styled.div `
  background: var(--bg-tertiary);
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
`;
const ContextIcon = styled.div `
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: linear-gradient(135deg, #9333ea 0%, #7c3aed 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 11px;
`;
const ContextText = styled.div `
  flex: 1;
  font-size: 11px;
  color: var(--text-secondary);

  .page-name {
    color: var(--text-primary);
    font-weight: 600;
  }
`;
// Quick actions section
const QuickActionsSection = styled.div `
  padding: 12px;
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
`;
const QuickActionsLabel = styled.div `
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-tertiary);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    font-size: 12px;
    color: #9333ea;
  }
`;
const QuickActionsGrid = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;
const QuickActionButton = styled.button `
  padding: 6px 12px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  color: var(--text-primary);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: linear-gradient(135deg, #9333ea 0%, #7c3aed 100%);
    color: white;
    border-color: transparent;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;
const MessagesContainer = styled.div `
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: var(--bg-secondary);

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: var(--bg-tertiary);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 3px;

    &:hover {
      background: var(--border-color-hover);
    }
  }
`;
const Message = styled.div `
  display: flex;
  align-items: flex-start;
  gap: 10px;
  animation: ${fadeIn} 0.3s ease;
  flex-direction: ${props => props.$isUser ? 'row-reverse' : 'row'};
`;
const MessageAvatar = styled.div `
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.$isUser
    ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
    : 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 14px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;
const MessageBubble = styled.div `
  max-width: 75%;
  padding: 10px 14px;
  border-radius: 12px;
  background: ${props => props.$isUser
    ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
    : 'var(--bg-primary)'};
  color: ${props => props.$isUser ? '#ffffff' : 'var(--text-primary)'};
  border: ${props => props.$isUser ? 'none' : '1px solid var(--border-color)'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  word-wrap: break-word;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;

  p {
    margin: 0 0 8px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    font-weight: 600;
    color: ${props => props.$isUser ? '#ffffff' : 'var(--text-primary)'};
  }

  & > * + * {
    margin-top: 8px;
  }
`;
const TypingIndicator = styled.div `
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 14px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  max-width: 70px;

  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--text-tertiary);
    animation: ${typingDots} 1.4s infinite;

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
`;
const InputContainer = styled.div `
  padding: 12px;
  border-top: 1px solid var(--border-color);
  background: var(--bg-primary);
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;
const Input = styled.input `
  flex: 1;
  padding: 10px 14px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 13px;
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: #9333ea;
    box-shadow: 0 0 0 3px rgba(147, 51, 234, 0.1);
  }

  &::placeholder {
    color: var(--text-tertiary);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
const SendButton = styled.button `
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => props.$disabled
    ? 'var(--bg-tertiary)'
    : 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)'};
  border: none;
  color: #ffffff;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all 0.2s;
  opacity: ${props => props.$disabled ? 0.5 : 1};

  &:hover {
    ${props => !props.$disabled && css `
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(147, 51, 234, 0.4);
    `}
  }

  &:active {
    transform: translateY(0);
  }
`;
const WelcomeMessage = styled.div `
  text-align: center;
  padding: 16px;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.6;

  strong {
    color: var(--text-primary);
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
  }
`;
const SuggestionsSection = styled.div `
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed var(--border-color);
`;
const SuggestionsLabel = styled.div `
  font-size: 11px;
  color: var(--text-tertiary);
  margin-bottom: 8px;
`;
const SuggestionsList = styled.div `
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const SuggestionItem = styled.button `
  text-align: left;
  padding: 8px 12px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #9333ea;
    color: var(--text-primary);
    background: var(--bg-tertiary);
  }
`;
const ErrorMessage = styled.div `
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #ef4444;
  padding: 10px;
  border-radius: 8px;
  font-size: 12px;
  text-align: center;
  margin: 8px 0;
`;
// Smart Search Action Card - appears when search is detected
const SearchActionCard = styled.div `
  background: linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%);
  border: 1px solid rgba(147, 51, 234, 0.3);
  border-radius: 8px;
  padding: 12px;
  margin: 8px 0;
  animation: ${fadeIn} 0.3s ease;
`;
const SearchActionHeader = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  color: #9333ea;
  font-size: 12px;
  font-weight: 600;

  svg {
    font-size: 14px;
  }
`;
const SearchActionDescription = styled.div `
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: 10px;
  line-height: 1.4;
`;
const SearchActionButton = styled.button `
  width: 100%;
  padding: 8px 12px;
  background: linear-gradient(135deg, #9333ea 0%, #7c3aed 100%);
  border: none;
  border-radius: 6px;
  color: white;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(147, 51, 234, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;
// Save Note Modal
const SaveNoteOverlay = styled.div `
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  animation: ${fadeIn} 0.2s ease;
`;
const SaveNoteModal = styled.div `
  background: var(--bg-primary);
  border: 2px solid #9333ea;
  border-radius: 8px;
  padding: 20px;
  width: 90%;
  max-width: 350px;
  animation: ${fadeIn} 0.2s ease;
`;
const SaveNoteTitle = styled.div `
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: #9333ea;
  }
`;
const SaveNoteInput = styled.input `
  width: 100%;
  padding: 10px 12px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 13px;
  margin-bottom: 12px;

  &:focus {
    outline: none;
    border-color: #9333ea;
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;
const SaveNoteButtons = styled.div `
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;
const SaveNoteButton = styled.button `
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${props => props.$primary ? css `
    background: linear-gradient(135deg, #9333ea 0%, #7c3aed 100%);
    color: white;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(147, 51, 234, 0.4);
    }
  ` : css `
    background: var(--bg-tertiary);
    color: var(--text-primary);
    border: 1px solid var(--border-color);

    &:hover {
      background: var(--bg-secondary);
    }
  `}
`;
// Saved Notes List
const NotesListContainer = styled.div `
  padding: 16px;
  background: var(--bg-secondary);
`;
const NotesListHeader = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;
const NotesListTitle = styled.div `
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    color: #9333ea;
  }
`;
const NoteItem = styled.div `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #9333ea;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;
const NoteItemTitle = styled.div `
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const NoteItemDate = styled.div `
  font-size: 10px;
  color: var(--text-tertiary);
`;
const NoteItemTags = styled.div `
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-top: 6px;
`;
const NoteTag = styled.span `
  font-size: 9px;
  padding: 2px 6px;
  background: rgba(147, 51, 234, 0.15);
  color: #9333ea;
  border-radius: 4px;
`;
const NoteDeleteButton = styled.button `
  background: transparent;
  border: none;
  color: var(--text-tertiary);
  cursor: pointer;
  padding: 2px;
  font-size: 10px;
  transition: color 0.2s;

  &:hover {
    color: #ef4444;
  }
`;
const SaveSuccessMessage = styled.div `
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #22c55e;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 8px 0;
  animation: ${fadeIn} 0.3s ease;
`;
const ResumePrompt = styled.div `
  background: var(--bg-primary);
  border: 2px solid #9333ea;
  padding: 16px;
  border-radius: 8px;
  margin: 16px;
  text-align: center;
  animation: ${fadeIn} 0.3s ease;
`;
const ResumePromptTitle = styled.div `
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;
const ResumePromptText = styled.div `
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: 12px;
  line-height: 1.5;
`;
const ResumeButtons = styled.div `
  display: flex;
  gap: 8px;
  justify-content: center;
`;
const ResumeButton = styled.button `
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${props => props.$variant === 'primary' ? css `
    background: linear-gradient(135deg, #9333ea 0%, #7c3aed 100%);
    color: #ffffff;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(147, 51, 234, 0.4);
    }
  ` : css `
    background: var(--bg-tertiary);
    color: var(--text-primary);
    border: 1px solid var(--border-color);

    &:hover {
      background: var(--bg-primary);
      border-color: var(--border-color-hover);
    }
  `}

  &:active {
    transform: translateY(0);
  }
`;
// Export conversation button
const ExportButton = styled(HeaderButton) `
  &:hover {
    background: rgba(34, 197, 94, 0.3);
  }
`;
// LocalStorage key for conversation persistence
const CONVERSATION_STORAGE_KEY = 'royaldiamonds-chat-conversation';
const CONVERSATION_EXPIRY_DAYS = 7;
const saveConversation = (messages) => {
    if (messages.length === 0)
        return;
    const storage = {
        messages,
        timestamp: Date.now(),
        lastUpdated: new Date().toISOString()
    };
    try {
        localStorage.setItem(CONVERSATION_STORAGE_KEY, JSON.stringify(storage));
    }
    catch (error) {
        console.error('Failed to save conversation:', error);
    }
};
const loadConversation = () => {
    try {
        const stored = localStorage.getItem(CONVERSATION_STORAGE_KEY);
        if (!stored)
            return null;
        const storage = JSON.parse(stored);
        const expiryTime = CONVERSATION_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
        const isExpired = Date.now() - storage.timestamp > expiryTime;
        if (isExpired) {
            localStorage.removeItem(CONVERSATION_STORAGE_KEY);
            return null;
        }
        return storage;
    }
    catch (error) {
        console.error('Failed to load conversation:', error);
        return null;
    }
};
const clearConversation = () => {
    try {
        localStorage.removeItem(CONVERSATION_STORAGE_KEY);
    }
    catch (error) {
        console.error('Failed to clear conversation:', error);
    }
};
export const DiamondChatWidget = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showResumePrompt, setShowResumePrompt] = useState(false);
    const [savedConversation, setSavedConversation] = useState(null);
    const [pendingSearch, setPendingSearch] = useState(null);
    const [showSaveNoteModal, setShowSaveNoteModal] = useState(false);
    const [noteTitle, setNoteTitle] = useState('');
    const [showSaveSuccess, setShowSaveSuccess] = useState(false);
    const [showNotesList, setShowNotesList] = useState(false);
    const [savedNotes, setSavedNotes] = useState([]);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    // Get page context
    const pageContext = useChatContext();
    // Diamond search hook
    const { searchDiamonds } = useDiamondSearch();
    // Load saved notes
    useEffect(() => {
        setSavedNotes(loadChatNotes());
    }, []);
    // Check for saved conversation on mount
    useEffect(() => {
        const saved = loadConversation();
        if (saved && saved.messages.length > 0) {
            setSavedConversation(saved);
        }
    }, []);
    // Auto-save conversation when messages change
    useEffect(() => {
        if (messages.length > 0) {
            saveConversation(messages);
        }
    }, [messages]);
    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isLoading]);
    // Focus input when chat opens
    useEffect(() => {
        if (isOpen && inputRef.current && !showResumePrompt) {
            inputRef.current.focus();
        }
    }, [isOpen, showResumePrompt]);
    // Show resume prompt when opening chat if saved conversation exists
    useEffect(() => {
        if (isOpen && savedConversation && messages.length === 0 && !showResumePrompt) {
            setShowResumePrompt(true);
        }
    }, [isOpen, savedConversation, messages.length, showResumePrompt]);
    const handleSendMessage = useCallback(async (messageText) => {
        const text = messageText || inputValue.trim();
        if (!text || isLoading)
            return;
        const userMessage = {
            role: 'user',
            content: text
        };
        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setError(null);
        setIsLoading(true);
        // Check if this looks like a diamond search query
        const searchPatterns = [
            /show\s+me|find|search|looking\s+for|want|need/i,
            /diamond|carat|ct|clarity|color|cut|round|princess|oval|emerald|cushion/i,
            /under\s+\$|over\s+\$|budget|\$\d+/i,
        ];
        const isSearchQuery = searchPatterns.some(pattern => pattern.test(text));
        if (isSearchQuery) {
            const parsed = parseSearchQuery(text);
            if (parsed.confidence > 0.2 && Object.keys(parsed.filters).length > 0) {
                setPendingSearch(parsed);
            }
        }
        try {
            // Include page context in the API call
            const contextInfo = formatContextForAPI(pageContext);
            const response = await apiClient.post('/chat-diamonds', {
                messages: [...messages, userMessage],
                context: {
                    page: pageContext.path,
                    pageName: pageContext.pageName,
                    contextHint: pageContext.contextHint,
                    isAuthenticated: pageContext.isAuthenticated,
                    userRole: pageContext.userRole,
                    fullContext: contextInfo
                }
            });
            if (response.ok && response.data) {
                const assistantMessage = {
                    role: 'assistant',
                    content: response.data.message
                };
                setMessages(prev => [...prev, assistantMessage]);
            }
            else {
                const errorMsg = response.data?.details || response.error || 'Failed to get response';
                throw new Error(errorMsg);
            }
        }
        catch (err) {
            console.error('Chat error:', err);
            const errorMessage = err instanceof Error ? err.message : 'Sorry, I encountered an error. Please try again.';
            setError(errorMessage);
        }
        finally {
            setIsLoading(false);
        }
    }, [inputValue, isLoading, messages, pageContext]);
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };
    const handleQuickAction = (action) => {
        handleSendMessage(action.prompt);
    };
    const handleSuggestionClick = (suggestion) => {
        handleSendMessage(suggestion);
    };
    const handleResumeConversation = () => {
        if (savedConversation) {
            setMessages(savedConversation.messages);
            setShowResumePrompt(false);
        }
    };
    const handleStartNewConversation = () => {
        clearConversation();
        setMessages([]);
        setSavedConversation(null);
        setShowResumePrompt(false);
    };
    const handleExportConversation = () => {
        if (messages.length === 0)
            return;
        const exportText = messages.map(m => `${m.role === 'user' ? 'You' : 'Royal Diamonds AI'}: ${m.content}`).join('\n\n');
        const blob = new Blob([exportText], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `chat-export-${new Date().toISOString().split('T')[0]}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };
    const handleExecuteSearch = () => {
        if (pendingSearch) {
            searchDiamonds(pendingSearch.description);
            setPendingSearch(null);
        }
    };
    const handleDismissSearch = () => {
        setPendingSearch(null);
    };
    // Save note handlers
    const handleOpenSaveNote = () => {
        // Generate default title from first user message
        const firstUserMsg = messages.find(m => m.role === 'user');
        const defaultTitle = firstUserMsg
            ? firstUserMsg.content.substring(0, 50) + (firstUserMsg.content.length > 50 ? '...' : '')
            : 'Chat Note';
        setNoteTitle(defaultTitle);
        setShowSaveNoteModal(true);
    };
    const handleSaveNote = () => {
        if (!noteTitle.trim() || messages.length === 0)
            return;
        createChatNote(noteTitle.trim(), messages, {
            pageContext: pageContext.pageName,
        });
        setShowSaveNoteModal(false);
        setNoteTitle('');
        setShowSaveSuccess(true);
        setSavedNotes(loadChatNotes());
        // Hide success message after 3 seconds
        setTimeout(() => setShowSaveSuccess(false), 3000);
    };
    const handleDeleteNote = (noteId, e) => {
        e.stopPropagation();
        deleteChatNote(noteId);
        setSavedNotes(loadChatNotes());
    };
    const handleExportNote = (note) => {
        const text = exportNoteAsText(note);
        const blob = new Blob([text], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${note.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };
    const handleLoadNoteConversation = (note) => {
        setMessages(note.fullConversation);
        setShowNotesList(false);
    };
    if (!isOpen) {
        return (_jsx(ChatContainer, { "$isOpen": false, children: _jsx(MinimizedButton, { onClick: () => setIsOpen(true), title: "Chat with Royal Diamonds AI", children: _jsx(FaComments, {}) }) }));
    }
    return (_jsx(ChatContainer, { "$isOpen": true, children: _jsxs(ChatWindow, { "$isExpanded": isExpanded, children: [_jsxs(ChatHeader, { children: [_jsxs(ChatTitle, { children: [_jsx(FaRobot, { className: "icon" }), _jsxs("div", { className: "title-text", children: [_jsx("div", { children: "Royal Diamonds AI" }), _jsx("div", { className: "subtitle", children: "Your Diamond Expert" })] })] }), _jsxs(HeaderButtons, { children: [savedNotes.length > 0 && (_jsx(HeaderButton, { onClick: () => setShowNotesList(!showNotesList), title: "View saved notes", style: { background: showNotesList ? 'rgba(255, 255, 255, 0.2)' : undefined }, children: _jsx(FaStickyNote, {}) })), messages.length > 0 && (_jsxs(_Fragment, { children: [_jsx(HeaderButton, { onClick: handleOpenSaveNote, title: "Save as note", children: _jsx(FaBookmark, {}) }), _jsx(ExportButton, { onClick: handleExportConversation, title: "Export conversation", children: _jsx(FaFileExport, {}) }), _jsx(HeaderButton, { onClick: handleStartNewConversation, title: "Start new conversation", children: _jsx(FaPlus, {}) })] })), _jsx(ExpandButton, { onClick: () => setIsExpanded(!isExpanded), title: isExpanded ? "Normal size" : "Expand chat", children: isExpanded ? _jsx(FaCompress, {}) : _jsx(FaExpand, {}) }), _jsx(CloseButton, { onClick: () => setIsOpen(false), title: "Close chat", children: _jsx(FaTimes, {}) })] })] }), _jsxs(ContextBar, { children: [_jsx(ContextIcon, { children: _jsx(FaLightbulb, {}) }), _jsxs(ContextText, { children: ["Helping you on ", _jsx("span", { className: "page-name", children: pageContext.pageName })] })] }), messages.length === 0 && !showResumePrompt && pageContext.quickActions.length > 0 && (_jsxs(QuickActionsSection, { children: [_jsxs(QuickActionsLabel, { children: [_jsx(FaLightbulb, {}), " Quick Actions"] }), _jsx(QuickActionsGrid, { children: pageContext.quickActions.map(action => (_jsx(QuickActionButton, { onClick: () => handleQuickAction(action), children: action.label }, action.id))) })] })), _jsxs(MessagesContainer, { children: [showResumePrompt && savedConversation ? (_jsxs(ResumePrompt, { children: [_jsxs(ResumePromptTitle, { children: [_jsx(FaComments, {}), "Previous Conversation"] }), _jsxs(ResumePromptText, { children: ["You have ", savedConversation.messages.length, " messages from", ' ', new Date(savedConversation.timestamp).toLocaleDateString(), ".", _jsx("br", {}), "Continue or start fresh?"] }), _jsxs(ResumeButtons, { children: [_jsx(ResumeButton, { "$variant": "primary", onClick: handleResumeConversation, children: "Resume" }), _jsx(ResumeButton, { "$variant": "secondary", onClick: handleStartNewConversation, children: "Start New" })] })] })) : messages.length === 0 ? (_jsxs(WelcomeMessage, { children: [_jsx("strong", { children: "Hi! How can I help you today?" }), "I can assist with diamonds, jewelry, and navigating the site.", pageContext.suggestions.length > 0 && (_jsxs(SuggestionsSection, { children: [_jsx(SuggestionsLabel, { children: "Try asking:" }), _jsx(SuggestionsList, { children: pageContext.suggestions.map((suggestion, index) => (_jsx(SuggestionItem, { onClick: () => handleSuggestionClick(suggestion), children: suggestion }, index))) })] }))] })) : (_jsxs(_Fragment, { children: [messages.map((message, index) => (_jsxs(Message, { "$isUser": message.role === 'user', children: [_jsx(MessageAvatar, { "$isUser": message.role === 'user', children: message.role === 'user' ? _jsx(FaUser, {}) : _jsx(FaRobot, {}) }), _jsx(MessageBubble, { "$isUser": message.role === 'user', children: message.content })] }, index))), isLoading && (_jsxs(Message, { "$isUser": false, children: [_jsx(MessageAvatar, { "$isUser": false, children: _jsx(FaRobot, {}) }), _jsxs(TypingIndicator, { children: [_jsx("span", {}), _jsx("span", {}), _jsx("span", {})] })] })), pendingSearch && !isLoading && (_jsxs(SearchActionCard, { children: [_jsxs(SearchActionHeader, { children: [_jsx(FaGem, {}), "Diamond Search Detected"] }), _jsx(SearchActionDescription, { children: formatFiltersDescription(pendingSearch.filters) }), _jsxs(SearchActionButton, { onClick: handleExecuteSearch, children: [_jsx(FaSearch, {}), "Search Inventory"] })] }))] })), error && _jsx(ErrorMessage, { children: error }), _jsx("div", { ref: messagesEndRef })] }), _jsxs(InputContainer, { children: [_jsx(Input, { ref: inputRef, type: "text", placeholder: `Ask about ${pageContext.pageName.toLowerCase()}...`, value: inputValue, onChange: (e) => setInputValue(e.target.value), onKeyPress: handleKeyPress, disabled: isLoading }), _jsx(SendButton, { onClick: () => handleSendMessage(), "$disabled": !inputValue.trim() || isLoading, disabled: !inputValue.trim() || isLoading, children: _jsx(FaPaperPlane, {}) })] }), showNotesList && savedNotes.length > 0 && (_jsxs(NotesListContainer, { children: [_jsx(NotesListHeader, { children: _jsxs(NotesListTitle, { children: [_jsx(FaStickyNote, {}), "Saved Notes (", savedNotes.length, ")"] }) }), savedNotes.slice(0, 5).map(note => (_jsxs(NoteItem, { onClick: () => handleLoadNoteConversation(note), children: [_jsxs(NoteItemTitle, { children: [_jsxs("span", { children: [note.title.substring(0, 30), note.title.length > 30 ? '...' : ''] }), _jsx(NoteDeleteButton, { onClick: (e) => handleDeleteNote(note.id, e), title: "Delete note", children: _jsx(FaTrash, {}) })] }), _jsxs(NoteItemDate, { children: [new Date(note.createdAt).toLocaleDateString(), note.pageContext && ` · ${note.pageContext}`] }), note.tags.length > 0 && (_jsx(NoteItemTags, { children: note.tags.slice(0, 3).map((tag, i) => (_jsx(NoteTag, { children: tag }, i))) }))] }, note.id)))] })), showSaveNoteModal && (_jsx(SaveNoteOverlay, { onClick: () => setShowSaveNoteModal(false), children: _jsxs(SaveNoteModal, { onClick: e => e.stopPropagation(), children: [_jsxs(SaveNoteTitle, { children: [_jsx(FaBookmark, {}), "Save Conversation as Note"] }), _jsx(SaveNoteInput, { type: "text", placeholder: "Note title...", value: noteTitle, onChange: (e) => setNoteTitle(e.target.value), autoFocus: true, onKeyPress: (e) => e.key === 'Enter' && handleSaveNote() }), _jsxs(SaveNoteButtons, { children: [_jsx(SaveNoteButton, { onClick: () => setShowSaveNoteModal(false), children: "Cancel" }), _jsxs(SaveNoteButton, { "$primary": true, onClick: handleSaveNote, children: [_jsx(FaCheck, {}), " Save"] })] })] }) })), showSaveSuccess && (_jsxs(SaveSuccessMessage, { children: [_jsx(FaCheck, {}), "Note saved successfully!"] }))] }) }));
};
export default DiamondChatWidget;
